/**
 * Host layout for the hybrid tenancy model.
 *
 * `app.<baseDomain>` is the global login host: it has no workspace, so no tenant session can exist
 * there. Every workspace runs on its own host (`clinic1.<baseDomain>` or a stored tenant URL) and
 * that is where a session is actually issued.
 */
const normalizeDomain = (value: string | undefined): string | null => {
  const trimmed = (value || '').trim().toLowerCase().replace(/^\.+|\.+$/g, '');
  return trimmed.length > 0 ? trimmed : null;
};

const BASE_DOMAIN = normalizeDomain(process.env.REACT_APP_PUBLIC_BASE_DOMAIN);
const APP_HOST_LABEL = (process.env.REACT_APP_APP_HOST_LABEL || 'app').trim().toLowerCase() || 'app';
const DEV_GLOBAL_AUTH = String(process.env.REACT_APP_DEV_GLOBAL_AUTH || '').trim().toLowerCase() === 'true';

const currentHostname = (): string =>
  (typeof window === 'undefined' ? '' : window.location.hostname).trim().toLowerCase();

export const getBaseDomain = (): string | null => BASE_DOMAIN;

export const getAppHost = (): string | null => (BASE_DOMAIN ? `${APP_HOST_LABEL}.${BASE_DOMAIN}` : null);

export const getAppHostUrl = (): string | null => {
  const host = getAppHost();
  return host ? `https://${host}` : null;
};

export const buildWorkspaceUrl = (slug: string): string | null => {
  const normalized = slug.trim().toLowerCase();
  return BASE_DOMAIN && normalized ? `https://${normalized}.${BASE_DOMAIN}` : null;
};

/**
 * The deployed global host. Nothing here can hold a session, so the app must not try to restore one.
 */
export const isTenantlessAuthHost = (): boolean => {
  const appHost = getAppHost();
  return appHost !== null && currentHostname() === appHost;
};

/**
 * Local development stands in for the app host without needing DNS: the picker runs on localhost and
 * the handoff code is redeemed on that same origin instead of a workspace hostname.
 */
export const isDevGlobalAuthEnabled = (): boolean => DEV_GLOBAL_AUTH && !isTenantlessAuthHost();

export const isGlobalAuthHost = (): boolean => isTenantlessAuthHost() || isDevGlobalAuthEnabled();
